/**
 * AdminSupportTickets — inbound ticket queue for platform admins.
 * List (left) + detail modal (opens on row click) with reply-by-email.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  LifeBuoy, RefreshCw, Search, Send, Mail, Loader2, CheckCircle2,
  Clock, Archive, Circle, X, Sparkles, AlertTriangle, ShieldAlert,
  ArrowDownRight, Bot, ClipboardCheck, Zap, UserCheck, Timer, SlidersHorizontal,
} from 'lucide-react';
import { SlaChip, TriageBudgetMeter } from '@/components/admin/SupportSlaChips';
import SupportPolicyDialog from '@/components/admin/SupportPolicyDialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const STATUS_META = {
  open:        { label: 'Open',        icon: Circle,        color: 'text-amber-400',  chip: 'bg-amber-950/40 border-amber-800 text-amber-200' },
  in_progress: { label: 'In Progress', icon: Clock,         color: 'text-sky-400',    chip: 'bg-sky-950/40 border-sky-800 text-sky-200' },
  resolved:    { label: 'Resolved',    icon: CheckCircle2,  color: 'text-emerald-400',chip: 'bg-emerald-950/40 border-emerald-800 text-emerald-200' },
  closed:      { label: 'Closed',      icon: Archive,       color: 'text-slate-400',  chip: 'bg-slate-800 border-slate-700 text-slate-300' },
};

const CATEGORY_META = {
  bug:             { label: 'Bug Report',       chip: 'bg-rose-950/40 border-rose-800 text-rose-200' },
  feature_request: { label: 'Feature Request',  chip: 'bg-sky-950/40 border-sky-800 text-sky-200' },
  feedback:        { label: 'Feedback',         chip: 'bg-amber-950/40 border-amber-800 text-amber-200' },
  billing:         { label: 'Billing',          chip: 'bg-violet-950/40 border-violet-800 text-violet-200' },
  other:           { label: 'Other',            chip: 'bg-slate-800 border-slate-700 text-slate-300' },
};

const SEVERITY_META = {
  critical: { label: 'Critical', icon: ShieldAlert,     chip: 'bg-rose-950/50 border-rose-700 text-rose-200' },
  high:     { label: 'High',     icon: AlertTriangle,   chip: 'bg-orange-950/50 border-orange-700 text-orange-200' },
  medium:   { label: 'Medium',   icon: Circle,          chip: 'bg-amber-950/40 border-amber-800 text-amber-200' },
  low:      { label: 'Low',      icon: ArrowDownRight,  chip: 'bg-slate-800 border-slate-600 text-slate-300' },
};

const TRIAGE_CLASS_LABELS = {
  bug_report: 'Bug report',
  feature_request: 'Feature request',
  how_to_question: 'How-to question',
  password_or_login_help: 'Login help',
  billing_question: 'Billing question',
  account_change: 'Account change',
  thanks_or_praise: 'Thanks / praise',
  spam_or_irrelevant: 'Spam / irrelevant',
  other: 'Other',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffMs = now - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const StatusChip = ({ status, testId }) => {
  const meta = STATUS_META[status] || STATUS_META.open;
  const Icon = meta.icon;
  return (
    <Badge className={`${meta.chip} border font-medium text-[10px] uppercase tracking-wider`} data-testid={testId}>
      <Icon className="h-3 w-3 mr-1" />
      {meta.label}
    </Badge>
  );
};

const CategoryChip = ({ category }) => {
  const meta = CATEGORY_META[category] || CATEGORY_META.other;
  return (
    <Badge className={`${meta.chip} border text-[10px]`}>{meta.label}</Badge>
  );
};

const SeverityChip = ({ severity, testId }) => {
  const meta = SEVERITY_META[severity];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <Badge
      className={`${meta.chip} border font-medium text-[10px] uppercase tracking-wider`}
      data-testid={testId}
    >
      <Icon className="h-3 w-3 mr-1" />
      {meta.label}
    </Badge>
  );
};

// ---------- AI triage panel (inside the detail modal) ----------
const TriagePanel = ({ ticket, onUseDraft, onUpdated }) => {
  const [running, setRunning] = useState(false);
  const triage = ticket.triage;
  // Derived from the reply thread, not from triage.auto_sent — a manual re-run
  // overwrites the triage block, but the sent reply is permanent history.
  const autoReplied = (ticket.replies || []).some((r) => r.auto);

  const run = async (forceReal = false) => {
    setRunning(true);
    try {
      const r = await api.post(
        `/support/tickets/${ticket.id}/triage${forceReal ? '?force_real=true' : ''}`,
      );
      onUpdated?.(r.data?.ticket);
      toast.success(
        forceReal ? 'Triaged with the live model' : 'Ticket re-triaged',
        { id: 'triage-done' },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Triage failed', { id: 'triage-fail' });
    } finally {
      setRunning(false);
    }
  };

  if (!triage) {
    return (
      <div
        className="rounded-md border border-dashed border-[#1F2A4D] bg-[#0A1024] p-4 text-center"
        data-testid="admin-ticket-triage-empty"
      >
        <Bot className="h-6 w-6 text-slate-600 mx-auto mb-2" />
        <p className="text-xs text-slate-400 mb-3">
          {ticket.triage_error
            ? `Triage failed: ${ticket.triage_error}`
            : 'No AI triage on this ticket yet.'}
        </p>
        <Button
          size="sm"
          onClick={() => run(false)}
          disabled={running}
          className="bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="admin-ticket-triage-run"
        >
          {running
            ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Triaging…</>
            : <><Sparkles className="h-3.5 w-3.5 mr-2" /> Triage with AI</>}
        </Button>
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-violet-900/50 bg-violet-950/10 p-3"
      data-testid="admin-ticket-triage-panel"
    >
      <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[10px] uppercase tracking-wider text-violet-300 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> AI triage
          </p>
          <SeverityChip severity={triage.severity} testId="admin-ticket-triage-severity" />
          <Badge
            className="bg-[#0A1024] border-[#1F2A4D] text-slate-300 border text-[10px]"
            data-testid="admin-ticket-triage-subsystem"
          >
            {triage.subsystem}
          </Badge>
          <Badge className="bg-[#0A1024] border-[#1F2A4D] text-slate-400 border text-[10px]">
            {TRIAGE_CLASS_LABELS[triage.triage_class] || triage.triage_class}
          </Badge>
          <span className="text-[10px] text-slate-500">{triage.confidence}% confidence</span>
          {triage.mock && (
            <Badge className="bg-slate-800 border-slate-600 text-slate-400 border text-[10px]">
              preview mock
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => run(false)}
          disabled={running}
          className="h-7 text-[11px] text-violet-300 hover:bg-violet-950/40"
          data-testid="admin-ticket-triage-rerun"
        >
          {running
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <><RefreshCw className="h-3 w-3 mr-1" /> Re-run</>}
        </Button>
      </div>

      {triage.rationale && (
        <p className="text-xs text-slate-300 leading-relaxed mb-2" data-testid="admin-ticket-triage-rationale">
          {triage.rationale}
        </p>
      )}

      {(triage.tags || []).length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {triage.tags.map((t) => (
            <span key={t} className="text-[10px] text-violet-200 bg-violet-950/40 border border-violet-900/60 rounded px-1.5 py-0.5">
              #{t}
            </span>
          ))}
        </div>
      )}

      {triage.draft_reply && (
        <div className="rounded border border-[#1F2A4D] bg-[#0A1024] p-2.5">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Drafted reply</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onUseDraft(triage.draft_reply)}
              className="h-6 text-[11px] border-[#1F2A4D] text-slate-200 hover:bg-[#0F1730]"
              data-testid="admin-ticket-triage-use-draft"
            >
              <ClipboardCheck className="h-3 w-3 mr-1" /> Use draft
            </Button>
          </div>
          <p className="text-xs text-slate-300 whitespace-pre-wrap" data-testid="admin-ticket-triage-draft">
            {triage.draft_reply}
          </p>
        </div>
      )}

      <p
        className="text-[10px] text-slate-500 mt-2 flex items-start gap-1"
        data-testid="admin-ticket-triage-autosend"
      >
        <Zap className={`h-3 w-3 mt-0.5 shrink-0 ${triage.auto_send_eligible ? 'text-emerald-400' : 'text-slate-600'}`} />
        {autoReplied
          ? 'An AI auto-reply has already gone out on this thread — further replies are yours to send.'
          : triage.auto_send_eligible
            ? `Eligible for auto-send — ${triage.auto_send_reason}. Sends only while the auto-reply policy is on.`
            : `Draft only — ${triage.auto_send_reason}.`}
      </p>
    </div>
  );
};

// ---------- Reply Modal ----------
const TicketReplyDialog = ({ ticket, open, onOpenChange, onUpdated }) => {
  const [body, setBody] = useState('');
  const [markResolved, setMarkResolved] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) { setBody(''); setMarkResolved(false); }
  }, [open, ticket?.id]);

  if (!ticket) return null;
  const replies = ticket.replies || [];

  const submit = async () => {
    if (body.trim().length < 10) {
      toast.error('Reply must be at least 10 characters.', { id: 'reply-too-short' });
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/support/tickets/${ticket.id}/reply`, {
        body: body.trim(),
        mark_resolved: markResolved,
      });
      const dispatched = r.data?.email_dispatched;
      toast.success(
        dispatched
          ? `Reply sent to ${ticket.email}`
          : `Reply saved (email delivery pending — check SendGrid).`,
        { id: 'reply-sent' },
      );
      onUpdated?.(r.data?.ticket);
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to send reply';
      toast.error(typeof msg === 'string' ? msg : 'Failed to send reply', { id: 'reply-error' });
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (nextStatus) => {
    try {
      const r = await api.patch(`/support/tickets/${ticket.id}`, { status: nextStatus });
      toast.success(`Status set to ${STATUS_META[nextStatus]?.label || nextStatus}`, {
        id: 'ticket-status-updated',
      });
      onUpdated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Status update failed', { id: 'ticket-status-fail' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl bg-[#0F1730] max-h-[90vh] overflow-y-auto"
        data-testid="admin-ticket-detail-modal"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-slate-100 pr-6">{ticket.subject}</DialogTitle>
              <DialogDescription className="text-slate-400 text-xs mt-1 flex flex-wrap items-center gap-2">
                <span>{ticket.email}</span>
                {ticket.full_name && <span>· {ticket.full_name}</span>}
                <span>· {formatDate(ticket.created_at)}</span>
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <SlaChip sla={ticket.sla} testId="admin-ticket-sla-chip" />
              <StatusChip status={ticket.status} testId="admin-ticket-status-chip" />
              <CategoryChip category={ticket.category} />
            </div>
          </div>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          {/* AI triage */}
          <TriagePanel ticket={ticket} onUseDraft={setBody} onUpdated={onUpdated} />

          {/* Original message */}
          <div className="rounded-md border border-[#1F2A4D] bg-[#0A1024] p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Original message</p>
            <p className="text-sm text-slate-200 whitespace-pre-wrap" data-testid="admin-ticket-original-message">
              {ticket.message}
            </p>
            {ticket.source_path && (
              <p className="mt-2 text-[10px] text-slate-500">Source: <code className="text-slate-400">{ticket.source_path}</code></p>
            )}
          </div>

          {/* Reply history */}
          {replies.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                Reply history ({replies.length})
              </p>
              <div className="space-y-2">
                {replies.map((r) => (
                  <div key={r.id} className="rounded-md border border-emerald-900/40 bg-emerald-950/10 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-emerald-300 flex items-center gap-1.5">
                        {r.admin_email}
                        {r.auto && (
                          <Badge className="bg-violet-950/40 border-violet-800 text-violet-200 border text-[9px] uppercase tracking-wider">
                            <Bot className="h-2.5 w-2.5 mr-1" /> AI auto-reply
                          </Badge>
                        )}
                      </p>
                      <p className="text-[10px] text-slate-500">
                        {formatDate(r.sent_at)} · {r.email_sent ? 'delivered' : 'delivery pending'}
                      </p>
                    </div>
                    <p className="text-sm text-slate-200 whitespace-pre-wrap">{r.body}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reply composer */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Send reply to {ticket.email}</p>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={`Hi ${ticket.full_name || 'there'},\n\nThanks for reaching out. …`}
              rows={7}
              maxLength={10000}
              className="bg-[#0A1024] border-[#1F2A4D] text-slate-100 placeholder:text-slate-500 resize-none"
              data-testid="admin-ticket-reply-body"
            />
            <div className="flex items-center justify-between mt-2 text-[10px] text-slate-500">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={markResolved}
                  onChange={(e) => setMarkResolved(e.target.checked)}
                  className="accent-emerald-500"
                  data-testid="admin-ticket-mark-resolved"
                />
                <span>Mark as resolved after sending</span>
              </label>
              <span>{body.length}/10000</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 mt-2 sm:justify-between">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="border-[#1F2A4D] text-slate-200 hover:bg-[#0A1024]"
                data-testid="admin-ticket-status-menu"
              >
                Change status
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                Set status to
              </DropdownMenuLabel>
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => updateStatus(key)}
                  disabled={ticket.status === key}
                  className="cursor-pointer focus:bg-slate-800"
                  data-testid={`admin-ticket-status-${key}`}
                >
                  <meta.icon className={`h-3.5 w-3.5 mr-2 ${meta.color}`} />
                  {meta.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-[#1F2A4D]" />
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="text-slate-300 hover:bg-[#0A1024]"
              onClick={() => onOpenChange(false)}
              data-testid="admin-ticket-close"
            >
              Close
            </Button>
            <Button
              onClick={submit}
              disabled={sending}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="admin-ticket-send-reply"
            >
              {sending ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Sending…</>
                       : <><Send className="h-3.5 w-3.5 mr-2" /> Send reply</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Main page ----------
const AdminSupportTickets = () => {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('severity');
  const [needsHuman, setNeedsHuman] = useState(false);
  const [needsHumanCount, setNeedsHumanCount] = useState(0);
  const [slaRisk, setSlaRisk] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [slaCounts, setSlaCounts] = useState({ at_risk: 0, breached: 0 });
  const [slaRiskCount, setSlaRiskCount] = useState(0);
  const [policyOpen, setPolicyOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200, sort: sortBy };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (severityFilter !== 'all') params.severity = severityFilter;
      if (needsHuman) params.view = 'needs_human';
      else if (slaRisk) params.view = 'sla_risk';
      const r = await api.get('/support/tickets', { params });
      setTickets(r.data?.tickets || []);
      setCounts(r.data?.counts_by_status || {});
      setNeedsHumanCount(r.data?.counts_by_view?.needs_human || 0);
      setSlaCounts(r.data?.counts_by_sla || { at_risk: 0, breached: 0 });
      setSlaRiskCount(r.data?.counts_by_view?.sla_risk || 0);
      if (r.data?.triage_budget) {
        setSettings((prev) => ({ ...(prev || {}), triage_budget: r.data.triage_budget }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load tickets', { id: 'load-tickets-fail' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, severityFilter, sortBy, needsHuman, slaRisk]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/support/settings')
      .then((r) => setSettings(r.data))
      .catch(() => setSettings({ auto_reply_enabled: false }));
  }, []);

  const toggleAutoReply = async (next) => {
    setSavingSettings(true);
    setSettings((prev) => ({ ...(prev || {}), auto_reply_enabled: next }));
    try {
      const r = await api.patch('/support/settings', { auto_reply_enabled: next });
      setSettings(r.data);
      toast.success(
        next
          ? 'Auto-reply ON — trivial, high-confidence tickets will answer themselves.'
          : 'Auto-reply OFF — every AI draft waits for you.',
        { id: 'autoreply-toggled' },
      );
    } catch (e) {
      setSettings((prev) => ({ ...(prev || {}), auto_reply_enabled: !next }));
      toast.error(e?.response?.data?.detail || 'Could not update policy', { id: 'autoreply-fail' });
    } finally {
      setSavingSettings(false);
    }
  };

  const openTicket = (t) => { setSelected(t); setDetailOpen(true); };

  const onTicketUpdated = (updated) => {
    if (!updated) return;
    setSelected(updated);
    setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    load(); // refresh counts_by_status
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return tickets;
    const term = q.trim().toLowerCase();
    return tickets.filter((t) =>
      (t.subject || '').toLowerCase().includes(term)
      || (t.email || '').toLowerCase().includes(term)
      || (t.full_name || '').toLowerCase().includes(term)
      || (t.message || '').toLowerCase().includes(term),
    );
  }, [tickets, q]);

  const totalCount = Object.values(counts).reduce((s, n) => s + (n || 0), 0);
  const openCount = counts.open || 0;
  const inProgressCount = counts.in_progress || 0;
  const resolvedCount = counts.resolved || 0;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-support-tickets-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-rose-400" /> Support Tickets
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Inbound messages from Contact Support forms on the dashboard + landing page.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <TriageBudgetMeter
            budget={settings?.triage_budget}
            onClick={() => setPolicyOpen(true)}
          />
          <div
            className="flex items-center gap-2.5 rounded-md border border-[#1F2A4D] bg-[#0F1730] px-3 py-2"
            data-testid="admin-support-autoreply-card"
          >
            <Zap className={`h-3.5 w-3.5 ${settings?.auto_reply_enabled ? 'text-emerald-400' : 'text-slate-500'}`} />
            <div className="leading-tight">
              <p className="text-[11px] font-medium text-slate-200">AI auto-reply</p>
              <p className="text-[10px] text-slate-500">
                {settings?.triage_budget?.auto_replies_paused
                  ? 'Paused — budget reached'
                  : settings?.auto_reply_enabled
                    ? 'On — trivial tickets answer themselves'
                    : 'Off — drafts wait for you'}
              </p>
            </div>
            <Switch
              checked={!!settings?.auto_reply_enabled}
              onCheckedChange={toggleAutoReply}
              disabled={savingSettings || settings === null}
              data-testid="admin-support-autoreply-toggle"
            />
          </div>
          <Button
            variant="outline"
            className="border-[#1F2A4D] text-slate-200"
            onClick={() => setPolicyOpen(true)}
            data-testid="admin-support-policy-open"
          >
            <SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Policy
          </Button>
          <Button
            variant="outline"
            className="border-[#1F2A4D] text-slate-200"
            onClick={load}
            data-testid="admin-support-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          { key: 'all', label: 'Total', count: totalCount, color: 'text-slate-100' },
          { key: 'open', label: 'Open', count: openCount, color: 'text-amber-400' },
          { key: 'in_progress', label: 'In Progress', count: inProgressCount, color: 'text-sky-400' },
          { key: 'resolved', label: 'Resolved', count: resolvedCount, color: 'text-emerald-400' },
        ].map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setStatusFilter(s.key)}
            className={`text-left rounded-md border p-3 transition-colors ${
              statusFilter === s.key
                ? 'border-rose-800 bg-rose-950/20'
                : 'border-[#1F2A4D] bg-[#0F1730] hover:bg-[#0A1024]'
            }`}
            data-testid={`admin-support-stat-${s.key}`}
          >
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{s.label}</p>
            <p className={`text-2xl font-bold mt-0.5 ${s.color}`}>{s.count}</p>
          </button>
        ))}
      </div>

      {/* Filters row */}
      <Card className="mb-4">
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="h-4 w-4 text-slate-500 absolute left-3 top-2.5" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search subject, message, name, or email"
              className="pl-9 bg-[#0A1024] border-[#1F2A4D] text-slate-100 placeholder:text-slate-500"
              data-testid="admin-support-search"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger
              className="w-48 bg-[#0A1024] border-[#1F2A4D] text-slate-100"
              data-testid="admin-support-category-filter"
            >
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(CATEGORY_META).map(([key, m]) => (
                <SelectItem key={key} value={key}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger
              className="w-44 bg-[#0A1024] border-[#1F2A4D] text-slate-100"
              data-testid="admin-support-severity-filter"
            >
              <SelectValue placeholder="All severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              {Object.entries(SEVERITY_META).map(([key, m]) => (
                <SelectItem key={key} value={key}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger
              className="w-52 bg-[#0A1024] border-[#1F2A4D] text-slate-100"
              data-testid="admin-support-sort"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="severity">Severity — worst first</SelectItem>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => { setNeedsHuman((v) => !v); setSlaRisk(false); }}
            className={needsHuman
              ? 'border-violet-700 bg-violet-950/30 text-violet-200 hover:bg-violet-950/50'
              : 'border-[#1F2A4D] text-slate-300 hover:bg-[#0A1024]'}
            data-testid="admin-support-needs-human"
          >
            <UserCheck className="h-3.5 w-3.5 mr-1.5" />
            Needs a human
            <span
              className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                needsHumanCount > 0
                  ? 'bg-violet-900/60 text-violet-100'
                  : 'bg-[#0A1024] text-slate-500'
              }`}
              data-testid="admin-support-needs-human-count"
            >
              {needsHumanCount}
            </span>
          </Button>
          <Button
            variant="outline"
            onClick={() => { setSlaRisk((v) => !v); setNeedsHuman(false); }}
            className={slaRisk
              ? 'border-rose-700 bg-rose-950/30 text-rose-200 hover:bg-rose-950/50'
              : 'border-[#1F2A4D] text-slate-300 hover:bg-[#0A1024]'}
            data-testid="admin-support-sla-risk"
          >
            <Timer className="h-3.5 w-3.5 mr-1.5" />
            SLA at risk
            <span
              className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                slaRiskCount > 0
                  ? 'bg-rose-900/60 text-rose-100'
                  : 'bg-[#0A1024] text-slate-500'
              }`}
              data-testid="admin-support-sla-risk-count"
            >
              {slaRiskCount}
            </span>
          </Button>
          {(statusFilter !== 'all' || categoryFilter !== 'all' || severityFilter !== 'all' || needsHuman || slaRisk || sortBy !== 'severity' || q) && (
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-slate-200"
              onClick={() => {
                setStatusFilter('all'); setCategoryFilter('all'); setSeverityFilter('all');
                setNeedsHuman(false); setSlaRisk(false); setSortBy('severity'); setQ('');
              }}
              data-testid="admin-support-clear-filters"
            >
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Ticket list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-rose-400" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-slate-400">
            {needsHuman ? (
              <>
                <UserCheck className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
                <p className="text-sm">
                  Nothing needs a human right now — every live ticket has been
                  answered or is one the AI can handle.
                </p>
              </>
            ) : (
              <>
                <Mail className="h-8 w-8 text-slate-600 mx-auto mb-3" />
                <p className="text-sm">
                  {tickets.length === 0
                    ? 'No support tickets yet. The queue is quiet.'
                    : 'No tickets match the current filters.'}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="admin-support-list">
          {filtered.map((t) => (
            <Card
              key={t.id}
              onClick={() => openTicket(t)}
              className="bg-[#0F1730] border-[#1F2A4D] hover:border-rose-800/50 transition-colors cursor-pointer"
              data-testid={`admin-support-row-${t.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-sm font-semibold text-slate-100 truncate">{t.subject}</h3>
                      {t.triage?.severity && <SeverityChip severity={t.triage.severity} />}
                      <SlaChip sla={t.sla} testId={`admin-support-sla-${t.id}`} />
                      <CategoryChip category={t.category} />
                      <StatusChip status={t.status} />
                      {(t.replies || []).length > 0 && (
                        <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px] border">
                          {(t.replies || []).length} repl{(t.replies || []).length === 1 ? 'y' : 'ies'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 line-clamp-1 mb-1">{t.message}</p>
                    <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                      <span>{t.email}</span>
                      {t.full_name && <span>· {t.full_name}</span>}
                      <span>· {formatDate(t.created_at)}</span>
                      {t.source_path && <span>· from <code>{t.source_path}</code></span>}
                      {t.triage?.subsystem && t.triage.subsystem !== 'unknown' && (
                        <span className="text-violet-300">· {t.triage.subsystem}</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TicketReplyDialog
        ticket={selected}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onUpdated={onTicketUpdated}
      />

      <SupportPolicyDialog
        open={policyOpen}
        onOpenChange={setPolicyOpen}
        settings={settings}
        onSaved={(next) => { setSettings(next); load(); }}
      />
    </div>
  );
};

export default AdminSupportTickets;
